import { DashboardController } from '../controller/DashboardController';
import { Features, Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { Widget } from '../../types';
import { ReportingPeriod, TimeRange } from '@nirvana/shared/dtos/types/shared/time';
import { BaseAdaptableWebComponent } from '../../framework/BaseAdaptableWebComponent';

export class DashboardWebComponent extends BaseAdaptableWebComponent {
  private static readonly INVALID_DASHBOARD_TYPE = -1;

  constructor() {
    super();
    this.initialSetup({
      controllerConstructor: DashboardController,
    });
  }

  get dashboardId(): string {
    const value = this.getAttribute('data-dashboard-id');
    return value ? value : '';
  }

  set dashboardId(value: string) {
    this.setAttribute('data-dashboard-id', value);
  }

  get dashboardType(): number {
    const value = this.getAttribute('data-dashboard-type');
    if (value) {
      return parseInt(value, 10);
    }
    return DashboardWebComponent.INVALID_DASHBOARD_TYPE;
  }

  set dashboardType(value: number) {
    this.setAttribute('data-dashboard-type', value.toString());
  }

  get widgets(): Widget[] {
    try {
      const value = this.getAttribute('data-widgets');
      if (!value) {
        return [];
      }
      return JSON.parse(value).widgets || [];
    } catch (e) {
      return [];
    }
  }

  set widgets(value: Widget[]) {
    this.setAttribute('data-widgets', JSON.stringify({ widgets: value }));
  }

  get dashboardTitle(): string {
    const value = this.getAttribute('data-dashboard-title');
    return value ? value : '';
  }

  set dashboardTitle(value: string) {
    this.setAttribute('data-dashboard-title', value);
  }

  get timeRange(): TimeRange | undefined {
    const value = this.getAttribute('data-time-range');
    return value ? JSON.parse(value) : undefined;
  }

  set timeRange(value: TimeRange | undefined) {
    if (value) {
      this.setAttribute('data-time-range', JSON.stringify(value));
    } else {
      this.removeAttribute('data-time-range');
    }
  }

  get reportingPeriod(): ReportingPeriod | undefined {
    const value = this.getAttribute('data-reporting-period');
    return value ? JSON.parse(value) : undefined;
  }

  set reportingPeriod(value: ReportingPeriod | undefined) {
    if (value) {
      this.setAttribute('data-reporting-period', JSON.stringify(value));
    } else {
      this.removeAttribute('data-reporting-period');
    }
  }

  get initialSelectedTarget(): Target {
    const value = this.getAttribute('data-initial-selected-target');
    return value ? JSON.parse(value) : {};
  }

  set initialSelectedTarget(value) {
    if (!value) {
      return;
    }
    this.setAttribute('data-initial-selected-target', JSON.stringify(value));
  }

  get initialAvailableCompanies(): Array<Target> {
    const values = this.getAttribute('data-initial-available-companies');
    return values ? JSON.parse(values) || [] : [];
  }

  set initialAvailableCompanies(value) {
    if (!value) {
      return;
    }
    this.setAttribute('data-initial-available-companies', JSON.stringify(value));
  }

  set isPartner(value: boolean) {
    if (!value) {
      return;
    }
    this.setAttribute('data-is-partner', JSON.stringify(value));
  }

  get isPartner(): boolean {
    const value = this.getAttribute('data-is-partner');
    return value ? JSON.parse(value) : false;
  }

  set canDrillDown(value: boolean) {
    if (!value) {
      return;
    }
    this.setAttribute('data-can-drill-down', JSON.stringify(value));
  }

  get canDrillDown(): boolean {
    const value = this.getAttribute('data-can-drill-down');
    return value ? JSON.parse(value) : false;
  }

  get features(): Features | undefined {
    const value = this.getAttribute('data-features');
    return value ? JSON.parse(value) : undefined;
  }

  set features(value: Features | undefined) {
    if (!value) {
      return;
    }
    this.setAttribute('data-features', JSON.stringify(value));
  }

  set hasReportsEnabled(value: boolean) {
    if (!value) {
      return;
    }
    this.setAttribute('data-has-reports-enabled', JSON.stringify(value));
  }

  get hasReportsEnabled(): boolean {
    const value = this.getAttribute('data-has-reports-enabled');
    return value ? JSON.parse(value) : false;
  }
}
